import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBackspace, FaLevelDownAlt, FaPlay, FaTimes, FaQuestionCircle, FaShareAlt } from 'react-icons/fa';
import useStore from '../../../../store';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import { useWordleLogic } from './useWordleLogic';
import { useArcadeShare } from '../../ui/useArcadeShare'; // Путь к новому хуку
import './Wordle.css';
import '../../ui/ArcadeShared.css';

const KEYBOARD_ROWS = [
  ['Й','Ц','У','К','Е','Н','Г','Ш','Щ','З','Х','Ъ'],
  ['Ф','Ы','В','А','П','Р','О','Л','Д','Ж','Э'],
  ['BACKSPACE', 'Я','Ч','С','М','И','Т','Ь','Б','Ю', 'ENTER', ]
];

const VALID_CHARS = KEYBOARD_ROWS.flat().filter(c => c !== 'ENTER' && c !== 'BACKSPACE');

const Wordle = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();
  const myId = store.ref.telegram.telegramUserId;

  const {
    state, typedWord, setTypedWord,
    showResults, setShowResults, finalData, sendAction,
    shakeRow, errorStamp
  } = useWordleLogic(socket);

  const [tooltip, setTooltip] = useState(null);

  // === ИНИЦИАЛИЗАЦИЯ НАШЕГО МОДУЛЯ ШЕРИНГА ===
  const { boardRef, share, isSharing, isCapturing } = useArcadeShare(socket);
  // ==========================================

  // Карта цветов клавиатуры
  const keyColors = useMemo(() => {
    const colors = {};
    state.history.forEach(round => {
      const chars = round.word.split('');
      chars.forEach((char, i) => {
        const status = round.match[i];
        const current = colors[char];
        // Приоритет: correct > present > absent
        if (status === 'correct') {
          colors[char] = 'correct';
        } else if (status === 'present' && current !== 'correct') {
          colors[char] = 'present';
        } else if (status === 'absent' && current !== 'correct' && current !== 'present') {
          colors[char] = 'absent';
        }
      });
    });
    return colors;
  }, [state.history]);

  // Функция для расчета статуса конкретной буквы целевого слова (для 7-го ряда)
  const getTargetCharStatus = (char, index) => {
    let isGreen = false;
    let isYellow = false;
    state.history.forEach(row => {
      if (row.word[index] === char) isGreen = true;
      if (row.word.includes(char)) isYellow = true;
    });
    if (isGreen) return 'correct';
    if (isYellow) return 'present';
    return 'missed'; // Красный
  };

  const onKeyPress = useCallback((key) => {
    if (state.completed || showResults) return;
    setTooltip(null); // Прячем тултип при печати

    if (key === 'ENTER') {
      if (typedWord.length === state.wordLength) {
        sendAction('submit', { word: typedWord });
      } else {
        window.croco?.haptic?.('error');
      }
    } else if (key === 'BACKSPACE') {
      setTypedWord(prev => prev.slice(0, -1));
      window.croco?.haptic?.('impactLight');
    } else if (VALID_CHARS.includes(key)) {
      if (typedWord.length < state.wordLength) {
        setTypedWord(prev => prev + key);
        window.croco?.haptic?.('selection');
      }
    }
  }, [state, typedWord, showResults, sendAction, setTypedWord]);

  // Поддержка физической клавиатуры с тактильным откликом на Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setTypedWord('');
        window.croco?.haptic?.('impactLight'); // <-- Добавили вибрацию
        return;
      }
      if (e.key === 'Enter') {
        onKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        onKeyPress('BACKSPACE');
      } else {
        const char = e.key.toUpperCase().replace(/Ё/g, 'Е');
        if (VALID_CHARS.includes(char)) {
          onKeyPress(char);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress, setTypedWord]);


  // Показ тултипа при клике на слово
  const handleRowClick = (e, historyRow) => {
    if (!historyRow || !historyRow.definition) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const isAbove = rect.top > window.innerHeight / 2;
    setTooltip({
      word: historyRow.word,
      text: historyRow.definition,
      y: isAbove ? rect.top - 15 : rect.bottom + 15,
      isAbove
    });
    window.croco?.haptic?.('selection');
  };

  // ИСПРАВЛЕННАЯ ЛОГИКА
  // Показываем 7-й ряд только если игра завершена, не выиграна, и сервер прислал targetWord
  const showTargetRow = state.completed && !state.won && state.targetWord;
  const totalRowsCount = showTargetRow ? state.maxAttempts + 1 : state.maxAttempts;

  // Экран результатов
  // ВАЖНО: Прячем оверлей, пока isCapturing === true
  if (showResults && finalData && !isCapturing) {
    const isWin = finalData.result === 'win' || state.won;
    const reportTitle = isWin ? t('arcade.wordle_win_title', 'КОД ВЗЛОМАН') : t('arcade.wordle_lose_title', 'ДОСТУП ЗАКРЫТ');
    const stampClass = isWin ? 'tr-stamp-ok' : 'tr-stamp-fail';
    const stampText = isWin ? t('arcade.wordle_stamp_win', 'ДОСТУП РАЗРЕШЕН') : t('arcade.wordle_stamp_lose', 'ПРОВАЛ');
    const myStats = finalData.my || {};

    // Форматируем время для UI внутри игры
    const timeTakenStr = myStats.time
      ? `${Math.floor(myStats.time / 60)}:${(myStats.time % 60).toString().padStart(2, '0')}`
      : '';

    return (
      <div className="tr-report-overlay">
         <div className="tr-report-card">
             <div className="tr-watermark">CIPHER</div>
             <div className="tr-report-header">
                <div className="tr-report-title">{reportTitle}</div>
             </div>

             <div className="tr-stats-grid">
                 {/* КРАСИВЫЙ ВЫВОД СЛОВА В КВАДРАТИКАХ */}
                 {/* Теперь показываем слово всегда. Если победа - буквы будут зелеными автоматически */}
                 {state.targetWord && (
                   <div className="report-target-word">
                     {state.targetWord.split('').map((char, i) => {
                       const status = getTargetCharStatus(char, i);
                       const bgColor = status === 'correct' ? '#4ade80' : status === 'present' ? '#fbbf24' : '#f87171';
                       const color = status === 'present' ? '#451a03' : '#fff';
                       return (
                         <div key={i} className="report-target-char" style={{ backgroundColor: bgColor, color: color }}>
                           {char}
                         </div>
                       );
                     })}
                   </div>
                 )}
                 <div className="tr-stat-row">
                     <span className="tr-stat-label">{t('arcade.wordduel_rank')}</span>
                     <span className="tr-stat-value">{t(`arcade.${myStats.rank || 'wordle_rank_rookie'}`)}</span>
                 </div>
                 {/* ВЫВОДИМ ПОПЫТКИ И ВРЕМЯ РЯДОМ */}
                 <div className="tr-stat-row">
                     <span className="tr-stat-label">{t('arcade.wordle_attempts', 'ПОПЫТОК')}</span>
                     <span className="tr-stat-value">
                        {state.attempts} / {state.maxAttempts}
                        {timeTakenStr && <span style={{fontSize: '14px', color: '#94a3b8', marginLeft: '6px'}}>({timeTakenStr})</span>}
                     </span>
                 </div>
                 <div className="tr-stat-row">
                     <span className="tr-stat-label">{t('arcade.safecracker_score')}</span>
                     <span className="tr-stat-value">{myStats.score || 0}</span>
                 </div>
             </div>

             <div className={`tr-result-stamp ${stampClass}`}>{stampText}</div>

             {finalData.leaderboard && (
               <div style={{width: '100%', marginBottom: '15px'}}>
                   <UnifiedLeaderboard results={finalData.leaderboard} myId={myId} t={t} />
               </div>
             )}

             <div className="tr-btn-group">
                 <button
                     className="tr-btn tr-btn-primary"
                     style={{ background: '#3b82f6', color: '#fff', marginBottom: '5px' }}
                     onClick={() => share(finalData.my, { backgroundColor: '#121826', ignoreClass: 'wordle-tooltip' })}
                     disabled={isSharing}
                 >
                     <FaShareAlt style={{marginRight: '8px'}} />
                     {isSharing ? t('arcade.share_preparing') : t('arcade.share')}
                 </button>

                 <button className="tr-btn tr-btn-secondary" onClick={() => setShowResults(false)}>
                     <FaPlay style={{marginRight: '8px'}} /> {t('arcade.stay')}
                 </button>

                 <button className="tr-btn tr-btn-primary" onClick={onClose}>
                     <FaTimes style={{marginRight: '8px'}} /> {t('arcade.close')}
                 </button>
             </div>
         </div>
      </div>
    );
  }

  // Отрисовка сетки 6xN
  const renderGrid = () => {
    const rows = [];
    for (let i = 0; i < state.maxAttempts; i++) {
      const isCurrentRow = i === state.history.length;
      const historyRow = state.history[i];
      const isShaking = isCurrentRow && shakeRow;

      const cells = [];
      for (let j = 0; j < state.wordLength; j++) {
        let char = '';
        let statusClass = '';

        if (historyRow) {
          char = historyRow.word[j];
          statusClass = historyRow.match[j]; // 'correct', 'present', 'absent'
        } else if (isCurrentRow && !state.completed && typedWord[j]) {
          // Строгая проверка !state.completed предотвращает рендер ввода после завершения
          char = typedWord[j];
          statusClass = 'filled';
        }

        cells.push(
          <div key={j} className={`wordle-cell ${statusClass}`}>
            {char}
          </div>
        );
      }

      rows.push(
        <div key={i} className={`wordle-row ${isShaking ? 'shake' : ''}`} onClick={(e) => handleRowClick(e, historyRow)}>
          {cells}
          {/* ИКОНКА ОПРЕДЕЛЕНИЯ (ПОЯВЛЯЕТСЯ ТОЛЬКО ЕСЛИ ЕСТЬ ОПРЕДЕЛЕНИЕ У СЛОВА) */}
          {historyRow && historyRow.definition && (
            <div className="wordle-question-icon">
               <FaQuestionCircle />
            </div>
          )}
        </div>
      );
    }

    // Рендер 7-го ряда (целевого слова) если нужно
    if (showTargetRow && state.targetWord) {
      const targetCells = [];
      for (let j = 0; j < state.wordLength; j++) {
        const char = state.targetWord[j];
        const statusClass = getTargetCharStatus(char, j);
        targetCells.push(<div key={`t${j}`} className={`wordle-cell ${statusClass}`}>{char}</div>);
      }

      // Формируем псевдо-объект истории, чтобы скормить его стандартной функции тултипа
      const targetMockHistory = {
        word: state.targetWord,
        definition: state.targetDefinition
      };

      rows.push(
        <div
          key="target-row"
          className="wordle-row reveal-row"
          onClick={(e) => handleRowClick(e, targetMockHistory)}
        >
          {targetCells}

          {/* Добавляем иконку вопроса, если определение есть */}
          {state.targetDefinition && (
            <div className="wordle-question-icon">
               <FaQuestionCircle />
            </div>
          )}
        </div>
      );
    }

    return rows;
  };

  const mins = Math.floor(timeLeft / 60);
  const secs = (timeLeft % 60).toString().padStart(2, '0');

  // Кнопку "Сдаться" показываем только если сервер прислал finalData (время вышло) и игра ещё не завершена
  const canSurrender = !!finalData && !state.completed;

  return (
    <div
      className="wordle-board-container"
      ref={boardRef} // <--- ДОБАВИТЬ ЭТО
      onClick={() => tooltip && setTooltip(null)}
    >
      {errorStamp && (
        <div className="wordle-stamp-overlay">
          <div className="wordle-stamp">{errorStamp}</div>
        </div>
      )}

      {tooltip && (
        <div
          className="wordle-tooltip"
          style={{
            [tooltip.isAbove ? 'bottom' : 'top']: tooltip.isAbove
              ? window.innerHeight - tooltip.y
              : tooltip.y
          }}
        >
          <span className="wordle-tooltip-word">{tooltip.word}</span>
          <div className="wordle-tooltip-def">{tooltip.text}</div>
        </div>
      )}

      <div className="wordle-header">
        <div className="wordle-title">
          {t('arcade.intro_wordle_title', 'ШИФРОВКА')}
        </div>
        <div className="wordle-stats">
          <span>{state.attempts}/{state.maxAttempts}</span>
          <span className="wordle-timer">{mins}:{secs}</span>
        </div>
      </div>

      <div className="wordle-grid-wrapper">
        <div
          className="wordle-grid"
          style={{
            '--cols': state.wordLength,
            '--rows': totalRowsCount
          }}
        >
          {renderGrid()}
        </div>
      </div>

      <div className="wordle-bottom-area">
        {canSurrender && (
          <button
            className="wordle-surrender-btn"
            onClick={(e) => {
              e.stopPropagation();
              sendAction('surrender');
            }}
          >
            {t('arcade.surrender', 'СДАТЬСЯ')}
          </button>
        )}

        <div className="wordle-keyboard">
          {KEYBOARD_ROWS.map((row, rIdx) => (
            <div key={rIdx} className="wk-row">
              {row.map((key) => {
                const isEnter = key === 'ENTER';
                const isBs = key === 'BACKSPACE';
                const colorClass = keyColors[key] || '';

                return (
                  <button
                    key={key}
                    className={`wk-key ${isEnter || isBs ? 'wide' : ''} ${colorClass}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onKeyPress(key);
                    }}
                  >
                    {isEnter ? (
                      <FaLevelDownAlt style={{ transform: 'rotate(90deg)' }} />
                    ) : isBs ? (
                      <FaBackspace size={20} />
                    ) : (
                      key
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wordle;